import { useEffect, useState } from "react";
import { FileText, Plus } from "lucide-react";
import {
  Alert,
  Button,
  Input,
  Skeleton,
  StatusPill,
  Textarea,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { storeErrorMessage } from "../error-message.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";

type PageRow = {
  id: string;
  slug: string;
  title: string;
  body: string;
  status: "draft" | "published";
  createdAt: string;
};

/** `null` = nothing selected; `"new"` = the unsaved draft; else a page id. */
type Selection = string | "new" | null;

/**
 * Store admin — content Pages (module 22).
 *
 * A page is free-form title + body a seller authors and links from the
 * storefront header/footer nav (the nav "Page" target resolves to
 * `/s/<store>/pages/<slug>`). Master-detail: the list on the left, an inline
 * editor on the right — no separate new/edit routes, because a page is a single
 * title + body with nothing to curate across screens.
 */
export function PagesListPage() {
  const canManage = usePermission("store.catalog.manage");
  const utils = trpc.useUtils();
  const list = trpc.storePages.list.useQuery({});
  const rows = list.data?.items ?? [];

  const [selected, setSelected] = useState<Selection>(null);

  return (
    <StoreAdminLayout
      breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Pages" }]}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Pages</h1>
          <p className="mt-1.5 text-sm text-muted">
            Content pages — About, Shipping, Terms — you can link from your header and footer.
          </p>
        </div>
        {canManage ? (
          <Button intent="primary" size="md" onClick={() => setSelected("new")}>
            <Plus size={14} aria-hidden />
            New page
          </Button>
        ) : null}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
        {/* ── List ──────────────────────────────────────────────── */}
        <div className="overflow-hidden rounded border border-line bg-paper shadow">
          {list.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <FileText size={24} className="mx-auto text-placeholder" aria-hidden />
              <p className="mt-2 text-sm text-muted">No pages yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.id)}
                    className={`flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-bg ${
                      selected === r.id ? "bg-bg" : ""
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-ink">{r.title}</span>
                      <span className="block truncate text-xs text-muted">/{r.slug}</span>
                    </span>
                    <StatusPill tone={r.status === "published" ? "ok" : "muted"} size="sm" dot>
                      {r.status === "published" ? "Live" : "Draft"}
                    </StatusPill>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Editor ────────────────────────────────────────────── */}
        <div>
          {selected === null ? (
            <div className="grid h-full min-h-[16rem] place-items-center rounded border border-dashed border-line bg-paper px-6 py-10 text-center">
              <p className="text-sm text-muted">
                {rows.length === 0
                  ? "Create your first page to link it from the storefront nav."
                  : "Select a page to edit, or create a new one."}
              </p>
            </div>
          ) : (
            <PageEditor
              key={selected}
              page={selected === "new" ? null : rows.find((r) => r.id === selected) ?? null}
              canManage={canManage}
              onSaved={(id) => {
                void utils.storePages.list.invalidate();
                setSelected(id);
              }}
              onDeleted={() => {
                void utils.storePages.list.invalidate();
                setSelected(null);
              }}
              onCancel={() => setSelected(null)}
            />
          )}
        </div>
      </div>
    </StoreAdminLayout>
  );
}

function PageEditor({
  page,
  canManage,
  onSaved,
  onCancel,
}: {
  page: PageRow | null;
  canManage: boolean;
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
}) {
  const utils = trpc.useUtils();
  const [title, setTitle] = useState(page?.title ?? "");
  const [body, setBody] = useState(page?.body ?? "");

  // Reset when the selected page changes (the `key` remount handles most of
  // it, but keep the effect so an in-place status flip re-syncs the fields).
  useEffect(() => {
    setTitle(page?.title ?? "");
    setBody(page?.body ?? "");
  }, [page?.id]);

  const create = trpc.storePages.create.useMutation();
  const update = trpc.storePages.update.useMutation();
  const setStatus = trpc.storePages.setStatus.useMutation();
  const busy = create.isPending || update.isPending || setStatus.isPending;

  async function save() {
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error("A page needs a title");
      return;
    }
    try {
      if (page) {
        await update.mutateAsync({ id: page.id, title: trimmed, body });
        onSaved(page.id);
        toast.success("Page saved");
      } else {
        const res = await create.mutateAsync({ title: trimmed, body });
        onSaved(res.id);
        toast.success("Page created", { description: "It's a draft — publish it when ready." });
      }
    } catch (err) {
      toast.error("Couldn't save page", { description: storeErrorMessage(err) });
    }
  }

  async function togglePublish() {
    if (!page) return;
    const next = page.status === "published" ? "draft" : "published";
    try {
      await setStatus.mutateAsync({ id: page.id, status: next });
      await utils.storePages.list.invalidate();
      onSaved(page.id);
      toast.success(next === "published" ? "Page published" : "Page unpublished");
    } catch (err) {
      toast.error("Couldn't update page", { description: storeErrorMessage(err) });
    }
  }

  return (
    <div className="rounded border border-line bg-paper p-5 shadow">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-ink">{page ? "Edit page" : "New page"}</h2>
        {page ? (
          <StatusPill tone={page.status === "published" ? "ok" : "muted"} size="sm" dot>
            {page.status === "published" ? "Live" : "Draft"}
          </StatusPill>
        ) : null}
      </div>

      {page ? (
        <p className="mt-1 text-xs text-muted">
          URL: <code className="text-ink">/pages/{page.slug}</code> — the slug is fixed once
          created, so links keep working after you edit the title.
        </p>
      ) : (
        <p className="mt-1 text-xs text-muted">
          The URL slug is generated from the title when you create the page.
        </p>
      )}

      <div className="mt-4 space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Title</span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="About us"
            maxLength={120}
            disabled={!canManage || busy}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Body</span>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your page content. Line breaks are preserved."
            rows={12}
            disabled={!canManage || busy}
          />
        </label>
      </div>

      {!canManage ? (
        <Alert className="mt-4" intent="info">
          You don't have permission to edit pages.
        </Alert>
      ) : (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button intent="primary" size="md" onClick={save} disabled={busy}>
            {page ? "Save changes" : "Create page"}
          </Button>
          {page ? (
            <Button intent="secondary" size="md" onClick={togglePublish} disabled={busy}>
              {page.status === "published" ? "Unpublish" : "Publish"}
            </Button>
          ) : null}
          <Button intent="ghost" size="md" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
